import type { RunningBackgroundTask, SessionEvent } from '../types/sessions.js';
import type {
  BackgroundWorkItem,
  BackgroundWorkStatus,
  ChatParticipant,
  ProcessedState,
  ProgressNode,
} from './types.js';

const MAIN_ARGUMENT_KEYS = ['url', 'query', 'q', 'path', 'file_path', 'command', 'goal', 'prompt', 'topic'];

function participantStatus(status: ChatParticipant['status']): BackgroundWorkStatus {
  switch (status) {
    case 'running':
    case 'idle':
      return 'running';
    case 'settled':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'stopped':
      return 'stopped';
  }
}

function firstLine(text: string | undefined): string {
  return (text ?? '').split('\n').find((line) => line.trim() !== '')?.trim() ?? '';
}

export function toolArgumentText(preview: string | undefined): string {
  const raw = (preview ?? '').trim();
  if (raw === '') return '';
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const args = parsed as Record<string, unknown>;
      for (const key of MAIN_ARGUMENT_KEYS) {
        if (typeof args[key] === 'string' && args[key] !== '') return firstLine(args[key]);
      }
      const value = Object.values(args).find((v): v is string => typeof v === 'string' && v !== '');
      if (value !== undefined) return firstLine(value);
    }
    if (typeof parsed === 'string') return firstLine(parsed);
  } catch {
    const match = raw.match(/"[a-z_]+"\s*:\s*"([^"]+)/i);
    if (match) return firstLine(match[1]);
  }
  return firstLine(raw);
}

function repliesByParticipant(events: readonly SessionEvent[]): Map<string, Array<{ at: string; messageId: string }>> {
  const replies = new Map<string, Array<{ at: string; messageId: string }>>();
  for (const e of events) {
    if (e.event_type !== 'assistant_message') continue;
    const participant = e.data.participant_id;
    const messageId = e.data.message_id;
    if (typeof participant !== 'string' || typeof messageId !== 'string') continue;
    const list = replies.get(participant) ?? [];
    list.push({ at: e.created_at, messageId });
    replies.set(participant, list);
  }
  return replies;
}

function firstReplyFrom(replies: Map<string, Array<{ at: string; messageId: string }>>, participant: string | null, startedAt: string): string | null {
  if (participant === null) return null;
  return replies.get(participant)?.find((r) => r.at >= startedAt)?.messageId ?? null;
}

function loadedNodes(state: ProcessedState): Map<string, ProgressNode> {
  const nodes = new Map<string, ProgressNode>();
  for (const run of state.reasoningRuns) for (const node of run.nodes) nodes.set(node.id, node);
  for (const [id, node] of state.detachedNodes) if (!nodes.has(id)) nodes.set(id, node);
  return nodes;
}

function byStartThenEnd(a: BackgroundWorkItem, b: BackgroundWorkItem): number {
  const aRunning = a.status === 'running';
  const bRunning = b.status === 'running';
  if (aRunning !== bRunning) return aRunning ? -1 : 1;
  if (aRunning) return (a.startedAt ?? '').localeCompare(b.startedAt ?? '');
  return (b.endedAt ?? '').localeCompare(a.endedAt ?? '');
}

export function deriveBackgroundWork(
  state: ProcessedState,
  participants: readonly ChatParticipant[],
  backgroundTasks: readonly RunningBackgroundTask[],
  events: readonly SessionEvent[],
): BackgroundWorkItem[] {
  const nodes = loadedNodes(state);
  const replies = repliesByParticipant(events);
  const primaryId = participants.find((p) => p.isPrimary)?.id ?? state.primaryParticipantId;
  const items: BackgroundWorkItem[] = [];

  for (const p of participants) {
    if (p.isPrimary || p.id === primaryId) continue;
    const node = state.participantNodes.get(p.id) ?? nodes.get(p.id);
    items.push({
      id: p.id,
      kind: 'agent',
      label: p.label,
      detail: firstLine(node?.promptPreview),
      tabParticipantId: p.id,
      status: participantStatus(p.status),
      replyMessageId: null,
      startedAt: node?.startedAt ?? null,
      endedAt: participantStatus(p.status) === 'running' ? null : (node?.endedAt ?? null),
    });
  }

  for (const node of nodes.values()) {
    if (node.background !== true || node.backgroundKind === 'participant') continue;
    if (node.participantId !== undefined && node.participantId === node.id) continue;
    const plan = node.backgroundKind === 'plan';
    items.push({
      id: node.id,
      kind: plan ? 'plan' : 'tool',
      label: plan ? 'Plan' : node.label,
      detail: plan ? firstLine(node.promptPreview) : toolArgumentText(node.promptPreview),
      tabParticipantId: node.participantId ?? primaryId,
      status: node.status,
      replyMessageId: firstReplyFrom(replies, node.participantId ?? primaryId, node.startedAt),
      startedAt: node.startedAt,
      endedAt: node.status === 'running' ? null : (node.endedAt ?? null),
    });
  }

  for (const task of backgroundTasks) {
    if (nodes.has(task.id)) continue;
    items.push({
      id: task.id,
      kind: task.kind,
      label: task.kind === 'plan' ? 'Plan' : 'Background tool',
      detail: '',
      tabParticipantId: task.owner_participant_id,
      status: 'running',
      replyMessageId: null,
      startedAt: null,
      endedAt: null,
    });
  }

  return items.sort(byStartThenEnd);
}
